import React, { useState, useEffect } from 'react';
import { Maybe } from '@/gql/graphql';
import { useTranslations } from 'use-intl';
import { GRAY_COLORS } from '@/UIlibrary/globalTheme';
import {
	Request,
	VanCreditReviewMutation,
	useVanCreditReviewMutation,
	ReviewOtherCreditsInput,
	ReviewCreditDetailsInput,
	ReviewRequestCallCommentsInput,
	ReviewPersonalInformationInput,
	ReviewMonthlyHouseExpensesInput,
	ReviewMonthlyBusinessExpensesInput,
	ReviewMonthlyIncomeInformationInput,
	ReviewActivityOrBusinessInformationInput,
	ReviewExpectedProfitForNewVehiclesInput
} from '@/graphql/__generated__/van-credits';
import { RequestStatusBadge } from './RequestStatusBadge';
import ActivityOrBusinessInformation from './VanCreditQuestionnaire/ActivityOrBusinessInformation';
import PersonalInformation from './VanCreditQuestionnaire/PersonalInformation';
import CreditBureauReport from './VanCreditQuestionnaire/CreditBureauReport';
import CreditDetails from './VanCreditQuestionnaire/CreditDetails';
import MonthlyNetIncome from './VanCreditQuestionnaire/MonthlyNetIncome';
import MonthlyBusinessExpenses from './VanCreditQuestionnaire/MonthlyBusinessExpenses';
import MonthlyIncomeInformation from './VanCreditQuestionnaire/MonthlyIncomeInformation';
import OtherCredits from './VanCreditQuestionnaire/OtherCredits';
import ValueOfAssets from './VanCreditQuestionnaire/ValueOfAssets';
import MonthlyHouseExpenses from './VanCreditQuestionnaire/MonthlyHouseExpenses';
import Button from '@/UIlibrary/Button/Button';
import { Trash } from 'react-feather';
import ConfirmModal from '@/stories/Components/ConfirmModal/ConfirmModal';
import toast from 'react-hot-toast';
import { useUpdateVanQuestionnaireStore } from '@/stores/useUpdateVanQuestionnaire';
import logger from '@/logger';
import TextArea from '@/UIlibrary/TextArea/TextArea';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { Tooltip } from '@/UIlibrary/Tooltip/Tooltip';
import ExpectedProfitForNewVehicles from './VanCreditQuestionnaire/ExpectedProfitForNewVehicles';
import * as Sentry from '@sentry/react';
import { useApolloClient } from '@/providers/MultiApolloProvider';
import { SCHEMAS } from '@/apolloClient';
import { Registry } from '@/graphql/__generated__/onboarding';

interface VanCreditQuestionnaireProps {
	requestData: Maybe<Request> | undefined;
	refetchData: () => Promise<any>;
	registry: Registry | null | undefined;
}

const cleanFormData = (data: any): any => {
	if (data === null || data === undefined) {
		return null;
	}

	if (data === '0.00 MXN') {
		return null;
	}

	if (typeof data === 'string') {
		return data.trim() === '' ? null : data;
	}

	if (typeof data === 'object' && !Array.isArray(data)) {
		const cleaned: any = {};
		for (const [key, value] of Object.entries(data)) {
			cleaned[key] = cleanFormData(value);
		}
		return cleaned;
	}

	return data;
};

const VanCreditQuestionnaire: React.FC<VanCreditQuestionnaireProps> = ({
	requestData,
	refetchData,
	registry
}) => {
	const t = useTranslations('VanCreditRequestQuestionnaire');
	const [isEditing, setIsEditing] = useState(false);
	const [formCallComments, setFormCallComments] = useState(requestData?.callComments || '');
	const [openConfirmChangesModal, setOpenConfirmChangesModal] = useState(false);
	const { getAllFormData, resetForm } = useUpdateVanQuestionnaireStore();
	const [formInputErrors, setFormInputErrors] = useState<Record<string, Record<string, string>>>(
		{}
	);

	const vanCreditRequestClient = useApolloClient(SCHEMAS.VAN_CREDITS);

	const extractMutationInvalidInputErrors = (
		mutationResult: VanCreditReviewMutation
	): Record<string, Record<string, string>> => {
		const inputErrors: Record<string, Record<string, string>> = {};

		if (!mutationResult) {
			return inputErrors;
		}

		Object.entries(mutationResult).forEach(([sectionKey, sectionValue]) => {
			if (
				typeof sectionValue !== 'object' ||
				sectionValue === null ||
				!('mutationErrors' in sectionValue) ||
				!Array.isArray(sectionValue.mutationErrors)
			) {
				return;
			}

			const invalidInputErrors = sectionValue.mutationErrors.filter(
				(error: any) => error?.__typename === 'InvalidInput'
			);

			if (invalidInputErrors.length === 0) {
				return;
			}

			if (!inputErrors[sectionKey]) {
				inputErrors[sectionKey] = {};
			}

			invalidInputErrors.forEach((error: any) => {
				if (error.field && error.message) {
					inputErrors[sectionKey][error.field] = error.message;

					logger.error(`Validation error in ${sectionKey}.${error.field}: ${error.message}`, {
						field: error.field,
						message: error.message,
						context: error.context,
						section: sectionKey
					});
				}
			});
		});

		if (Object.keys(inputErrors).length > 0) {
			toast.error(t('InputErrorUpdatingQuestionnaire'));
		}

		return inputErrors;
	};

	const [vanCreditReview, { loading }] = useVanCreditReviewMutation({
		client: vanCreditRequestClient,
		onCompleted: async (data: VanCreditReviewMutation) => {
			const allSectionWereSuccessfullyUpdated =
				data.reviewActivityOrBusinessInformation?.successful &&
				data.reviewCreditDetails?.successful &&
				data.reviewRequestCallComments?.successful &&
				data.reviewMonthlyBusinessExpenses?.successful &&
				data.reviewMonthlyHouseExpenses?.successful &&
				data.reviewMonthlyIncomeInformation?.successful &&
				data.reviewOtherCredits?.successful &&
				data.reviewPersonalInformation?.successful &&
				data.reviewValueOfAssets?.successful &&
				data.reviewExpectedProfitForNewVehicles?.successful;

			await refetchData();

			if (allSectionWereSuccessfullyUpdated) {
				logger.success('Van credit review approved succesfully');
				toast.success(t('QuestionnaireUpdatedSuccessfully'));
				setIsEditing(false);
				setFormInputErrors({});
				resetForm();
				return;
			}

			const inputErrors = extractMutationInvalidInputErrors(data);
			setFormInputErrors(inputErrors);

			if (Object.keys(inputErrors).length === 0) {
				toast.error(t('ErrorUpdatingQuestionnaire'), { duration: 10000 });
			}
		},
		onError: (error) => {
			logger.error('Error updating van credit questionnaire', error);
			toast.error(t('ErrorUpdatingQuestionnaire'));
			Sentry.captureException(error, {
				extra: {
					requestId: requestData?.requestId
				}
			});
		}
	});

	useEffect(() => {
		if (requestData?.callComments) {
			setFormCallComments(requestData.callComments);
		}
	}, [requestData]);

	const handleConfirmSavedChanges = async () => {
		const getFormData = getAllFormData();

		logger.debug('CHANGES', getFormData);

		// Clearing previous errors
		setFormInputErrors({});

		if (!formCallComments) {
			setFormInputErrors((prev) => ({
				...prev,
				reviewRequestCallComments: {
					call_comments: t('CallCommentsRequired')
				}
			}));
			setOpenConfirmChangesModal(false);
			toast.error(t('CallCommentsRequired'));
			return;
		}

		const cleanedFormData = cleanFormData(getFormData);

		await vanCreditReview({
			variables: {
				callCommentsInput: {
					requestId: requestData?.requestId,
					callComments: formCallComments
				} as ReviewRequestCallCommentsInput,
				personalInformationInput: {
					requestId: requestData?.requestId,
					...cleanedFormData?.personalInfo,
					otherFamilyDependants: cleanedFormData?.personalInfo?.otherFamilyDependants
						? Number(cleanedFormData?.personalInfo?.otherFamilyDependants)
						: null,
					childDependants: cleanedFormData?.personalInfo?.childDependants
						? Number(cleanedFormData?.personalInfo?.childDependants)
						: null
				} as ReviewPersonalInformationInput,
				activityOrBusinessInformationInput: {
					requestId: requestData?.requestId,
					...cleanedFormData?.activityOrBusiness
				} as ReviewActivityOrBusinessInformationInput,
				creditDetailsInput: {
					requestId: requestData?.requestId,
					...cleanedFormData?.creditDetails,
					term: cleanedFormData?.creditDetails?.term
						? Number(cleanedFormData?.creditDetails?.term)
						: null,
					downPaymentPercentage: cleanedFormData?.creditDetails?.downPaymentPercentage
						? Number(cleanedFormData?.creditDetails?.downPaymentPercentage)
						: null
				} as ReviewCreditDetailsInput,
				monthlyBusinessExpensesInput: {
					requestId: requestData?.requestId,
					...cleanedFormData?.monthlyBusinessExpenses
				} as ReviewMonthlyBusinessExpensesInput,
				monthlyHouseExpensesInput: {
					requestId: requestData?.requestId,
					...cleanedFormData?.monthlyHouseExpenses
				} as ReviewMonthlyHouseExpensesInput,
				monthlyIncomeInformationInput: {
					requestId: requestData?.requestId,
					...cleanedFormData?.monthlyIncome
				} as ReviewMonthlyIncomeInformationInput,
				otherCreditsInput: {
					requestId: requestData?.requestId,
					...cleanedFormData?.otherCredits
				} as ReviewOtherCreditsInput,
				valueOfAssetsInput: {
					requestId: requestData?.requestId,
					...cleanedFormData?.valueOfAssets
				} as ReviewOtherCreditsInput,
				expectedProfitForNewVehiclesInput: {
					requestId: requestData?.requestId,
					...cleanedFormData?.expectedProfitForNewVehicles
				} as ReviewExpectedProfitForNewVehiclesInput
			}
		});
		setOpenConfirmChangesModal(false);
	};

	const getConfirmChangesModalContent = () => {
		if (getAllFormData().creditDetails?.downPaymentAmount) {
			return t('DownPaymentAmountDisclaimer');
		}
		if (getAllFormData().creditDetails?.downPaymentPercentage) {
			return t('DownPaymentPercentageDisclaimer');
		}
		return null;
	};

	if (!requestData) {
		return null;
	}

	if (loading) {
		return (
			<div className="flex justify-center items-center h-screen">
				<div className="loader"></div>
			</div>
		);
	}

	const handleEditClick = () => {
		if (isEditing) {
			setOpenConfirmChangesModal(true);
		} else {
			setIsEditing(true);
		}
	};

	const handleDiscardChanges = () => {
		setIsEditing(false);
		resetForm();
		setFormCallComments(requestData?.callComments || '');
		setFormInputErrors({});
	};

	const canQuestionnaireBeEdited = true;

	const callCommentsHint = t('CallCommentsLength') + '. ' + formCallComments.length + '/3400';

	return (
		<div
			className="flex flex-col p-6 gap-5 rounded-lg mt-5 "
			style={{ backgroundColor: GRAY_COLORS.gray50 }}
		>
			<ConfirmModal
				isOpen={openConfirmChangesModal}
				title={t('SaveChanges')}
				onClose={() => setOpenConfirmChangesModal(false)}
				onCancel={() => setOpenConfirmChangesModal(false)}
				subtitle={t('SaveChangesMessage')}
				onConfirm={handleConfirmSavedChanges}
				confirmActionText={t('Save')}
			>
				{getConfirmChangesModalContent()}
			</ConfirmModal>
			<div className="flex flex-row sticky top-0 z-10 bg-white p-4 shadow-md">
				<div className="flex flex-row items-center justify-between gap-4 w-full">
					{isEditing ? (
						<div className="flex flex-col justify-start gap-2">
							<Typography weight="regular" color="subtitle" size="sm">
								* {t('QuestionnaireDisclaimer')}
							</Typography>
						</div>
					) : (
						<div className="flex grow" />
					)}
					<div className="flex flex-row items-center justify-end gap-4">
						{isEditing && (
							<div className="flex w-60">
								<Button
									label={t('DiscardChanges')}
									size="full"
									color="error"
									onClick={handleDiscardChanges}
									endIcon={<Trash />}
								/>
							</div>
						)}
						<div className="flex w-60">
							<Tooltip
								content={canQuestionnaireBeEdited ? '' : t('DisabledEditQuestionnaireTooltip')}
								position="bottom"
								id="edit-tooltip"
							>
								<Button
									label={isEditing ? t('SaveChanges') : t('EditQuestionnaire')}
									size="full"
									outlined={!isEditing}
									color={isEditing ? 'primary' : 'white'}
									onClick={handleEditClick}
									disabled={false} // TODO: Remove this once the questionnaire is editable
								/>
							</Tooltip>
						</div>
					</div>
				</div>
			</div>
			<div className="flex justify-end">
				{requestData?.requestStatus && (
					<RequestStatusBadge status={requestData.requestStatus} size="sm" />
				)}
			</div>
			<div className="flex flex-col gap-1 p-4 bg-white rounded-lg w-full">
				<div className="grow">
					<Typography weight="semibold">{t('CallCommentsTitle')}</Typography>
				</div>
				<div className="w-full">
					<TextArea
						rows={4}
						value={formCallComments}
						onChange={(prev) => setFormCallComments(prev.target.value)}
						placeholder={t('CallCommentsPlaceholder')}
						disabled={!isEditing}
						error={!!formInputErrors?.reviewRequestCallComments?.call_comments}
						hint={formInputErrors?.reviewRequestCallComments?.call_comments || callCommentsHint}
						maxChars={3400}
						strictMaxChars
					/>
				</div>
			</div>
			<MonthlyNetIncome data={requestData} title="MonthlyNetIncome" />
			<CreditDetails
				data={requestData.creditDetails}
				title="CreditDetails"
				isEditing={isEditing}
				inputErrors={formInputErrors.reviewCreditDetails}
			/>
			<CreditBureauReport
				data={requestData.creditBureauReport}
				title="CreditBureau"
				registry={registry}
			/>
			<OtherCredits
				data={requestData.otherCredits}
				title="OtherCredits"
				isEditing={isEditing}
				inputErrors={formInputErrors.reviewOtherCredits}
			/>
			<PersonalInformation
				data={requestData.personalInformation}
				title="PersonalInformation"
				isEditing={isEditing}
				inputErrors={formInputErrors.reviewPersonalInformation}
			/>
			<ActivityOrBusinessInformation
				data={requestData.activityOrBusinessInformation}
				title="ActivityInformation"
				isEditing={isEditing}
				inputErrors={formInputErrors.reviewActivityOrBusinessInformation}
			/>
			<MonthlyIncomeInformation
				data={requestData.monthlyIncomeInformation}
				title="MonthlyIncomeInformation"
				isEditing={isEditing}
				inputErrors={formInputErrors.reviewMonthlyIncomeInformation}
			/>
			<MonthlyBusinessExpenses
				data={requestData.monthlyBusinessExpenses}
				title="MonthlyBusinessExpenses"
				isEditing={isEditing}
				inputErrors={formInputErrors.reviewMonthlyBusinessExpenses}
			/>
			<MonthlyHouseExpenses
				data={requestData.monthlyHouseExpenses}
				title="MonthlyHomeExpenses"
				isEditing={isEditing}
				inputErrors={formInputErrors.reviewMonthlyHouseExpenses}
			/>
			<ValueOfAssets
				data={requestData.valueOfAssets}
				title="ValueOfAssets"
				isEditing={isEditing}
				inputErrors={formInputErrors.reviewValueOfAssets}
			/>
			<ExpectedProfitForNewVehicles
				data={requestData.expectedProfitForNewVehicles}
				title="ExpectedProfitForNewVehicles"
				isEditing={isEditing}
				inputErrors={formInputErrors.reviewExpectedProfitForNewVehicles}
			/>
		</div>
	);
};

export default VanCreditQuestionnaire;
